import { useState, useEffect, useRef } from 'react'
import { Loader2, Trash2, AlertTriangle, RefreshCcw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { db, batchTransact, ADMIN_ORG_ID } from '@/lib/db'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useProfile } from '@/hooks/useProfile'

/**
 * 数据库清理页面（临时工具）
 * 
 * 用于清理孤立数据：
 * - 没有对应项目的素材
 * - 没有对应项目的笔记
 * - 没有对应风格的模板
 * - 没有对应模板的槽位
 */
export function DatabaseCleanupPage() {
  const navigate = useNavigate()
  const { profile, isLoading: isProfileLoading } = useProfile()
  const [isCleaning, setIsCleaning] = useState(false)
  const [shouldScan, setShouldScan] = useState(true)
  const toastShownRef = useRef(false)

  // 权限检查
  useEffect(() => {
    if (!isProfileLoading && profile && profile.org_id !== ADMIN_ORG_ID) {
      if (!toastShownRef.current) {
        toast.error('您没有权限访问此页面')
        toastShownRef.current = true
      }
      navigate('/', { replace: true })
    }
  }, [profile, isProfileLoading, navigate])

  // 实时查询数据库
  const { data, isLoading: isDataLoading } = db.useQuery({
    projects: {},
    assets: {},
    notes: {},
    note_styles: {},
    note_templates: {},
    note_image_slots: {},
  })

  const isLoading = isProfileLoading || isDataLoading

  if (!isProfileLoading && profile?.org_id !== ADMIN_ORG_ID) {
    return null
  }

  const projects = data?.projects || []
  const assets = data?.assets || []
  const notes = data?.notes || []
  const styles = data?.note_styles || []
  const templates = data?.note_templates || []
  const slots = data?.note_image_slots || []

  // 计算数据大小（粗略估算）
  const estimateSize = (obj: any): number => {
    const str = JSON.stringify(obj)
    return new Blob([str]).size
  }

  const sizeStats = {
    projects: {
      count: projects.length,
      size: estimateSize(projects),
      avgSize: projects.length > 0 ? estimateSize(projects) / projects.length : 0,
    },
    assets: {
      count: assets.length,
      size: estimateSize(assets),
      avgSize: assets.length > 0 ? estimateSize(assets) / assets.length : 0,
    },
    notes: {
      count: notes.length,
      size: estimateSize(notes),
      avgSize: notes.length > 0 ? estimateSize(notes) / notes.length : 0,
    },
    styles: {
      count: styles.length,
      size: estimateSize(styles),
      avgSize: styles.length > 0 ? estimateSize(styles) / styles.length : 0,
    },
    templates: {
      count: templates.length,
      size: estimateSize(templates),
      avgSize: templates.length > 0 ? estimateSize(templates) / templates.length : 0,
    },
    slots: {
      count: slots.length,
      size: estimateSize(slots),
      avgSize: slots.length > 0 ? estimateSize(slots) / slots.length : 0,
    },
  }

  const totalSize = Object.values(sizeStats).reduce((sum, stat) => sum + stat.size, 0)
  
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
  }

  // 计算孤立数据
  const projectIds = new Set(projects.map((p: any) => p.id))
  const orphanedAssets = assets.filter((asset: any) => !projectIds.has(asset.project_id))
  const orphanedNotes = notes.filter((note: any) => !projectIds.has(note.project_id))

  const styleIds = new Set(styles.map((s: any) => s.id))
  const orphanedTemplates = templates.filter((tpl: any) => !styleIds.has(tpl.style_id))

  const templateIds = new Set(templates.map((t: any) => t.id))
  const orphanedSlots = slots.filter((slot: any) => !templateIds.has(slot.template_id))

  const report = {
    orphanedAssets: orphanedAssets.length,
    orphanedNotes: orphanedNotes.length,
    orphanedTemplates: orphanedTemplates.length,
    orphanedSlots: orphanedSlots.length,
    details: {
      assets: orphanedAssets,
      notes: orphanedNotes,
      templates: orphanedTemplates,
      slots: orphanedSlots,
    },
  }

  // 自动显示扫描结果
  useEffect(() => {
    if (!isLoading && shouldScan && (orphanedAssets.length > 0 || orphanedNotes.length > 0 || orphanedTemplates.length > 0 || orphanedSlots.length > 0)) {
      toast.success('发现孤立数据')
      setShouldScan(false)
    }
  }, [isLoading, shouldScan, orphanedAssets.length, orphanedNotes.length, orphanedTemplates.length, orphanedSlots.length])

  const cleanupDatabase = async () => {
    const totalOrphaned = report.orphanedAssets + report.orphanedNotes + report.orphanedTemplates + report.orphanedSlots

    if (totalOrphaned === 0) {
      toast.info('没有需要清理的数据')
      return
    }

    const confirmed = confirm(
      `确定要清理这些孤立数据吗？\n\n` +
      `- 孤立素材：${report.orphanedAssets} 个\n` +
      `- 孤立笔记：${report.orphanedNotes} 个\n` +
      `- 孤立模板：${report.orphanedTemplates} 个\n` +
      `- 孤立槽位：${report.orphanedSlots} 个\n\n` +
      `此操作不可恢复！`
    )

    if (!confirmed) return

    setIsCleaning(true)

    try {
      const txns: any[] = []

      // 删除孤立数据
      report.details.assets.forEach((asset: any) => {
        txns.push(db.tx.assets[asset.id].delete())
      })

      report.details.notes.forEach((note: any) => {
        txns.push(db.tx.notes[note.id].delete())
      })

      report.details.templates.forEach((tpl: any) => {
        txns.push(db.tx.note_templates[tpl.id].delete())
      })

      report.details.slots.forEach((slot: any) => {
        txns.push(db.tx.note_image_slots[slot.id].delete())
      })

      // 使用分批删除（避免超时）
      await batchTransact(txns)

      toast.success(`已清理 ${totalOrphaned} 条孤立数据`)
      setShouldScan(true)
    } catch (error) {
      console.error(error)
      toast.error('清理失败')
    } finally {
      setIsCleaning(false)
    }
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold">数据库清理工具</h1>
        <p className="text-muted-foreground mt-2">
          扫描并清理数据库中的孤立数据（没有关联关系的记录）
        </p>
      </div>

      <Card className="border-amber-200 bg-amber-50/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <CardTitle className="text-amber-900">⚠️ 注意</CardTitle>
          </div>
          <CardDescription className="text-amber-800">
            此工具会删除没有关联项目的素材和笔记，以及没有关联风格的模板和槽位。
            <br />
            建议在清理前备份重要数据。
          </CardDescription>
        </CardHeader>
      </Card>

      {/* 数据统计卡片 */}
      {!isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>数据库统计</CardTitle>
            <CardDescription>
              当前数据占用情况（客户端估算，实际服务器占用可能更大）
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* 项目统计 */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-blue-900">项目 (Projects)</h3>
                    <Badge variant="secondary">{sizeStats.projects.count} 个</Badge>
                  </div>
                  <p className="text-sm text-blue-700">
                    总大小: <span className="font-mono font-bold">{formatSize(sizeStats.projects.size)}</span>
                  </p>
                  {sizeStats.projects.count > 0 && (
                    <p className="text-xs text-blue-600 mt-1">
                      平均: {formatSize(sizeStats.projects.avgSize)}/个
                    </p>
                  )}
                </div>

                {/* 素材统计 */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-purple-900">素材 (Assets)</h3>
                    <Badge variant="secondary">{sizeStats.assets.count} 个</Badge>
                  </div>
                  <p className="text-sm text-purple-700">
                    总大小: <span className="font-mono font-bold">{formatSize(sizeStats.assets.size)}</span>
                  </p>
                  {sizeStats.assets.count > 0 && (
                    <p className="text-xs text-purple-600 mt-1">
                      平均: {formatSize(sizeStats.assets.avgSize)}/个
                    </p>
                  )}
                </div>

                {/* 笔记统计 */}
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-green-900">笔记 (Notes)</h3>
                    <Badge variant="secondary">{sizeStats.notes.count} 个</Badge>
                  </div>
                  <p className="text-sm text-green-700">
                    总大小: <span className="font-mono font-bold">{formatSize(sizeStats.notes.size)}</span>
                  </p>
                  {sizeStats.notes.count > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      平均: {formatSize(sizeStats.notes.avgSize)}/个
                    </p>
                  )}
                </div>

                {/* 风格统计 */}
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-orange-900">笔记风格 (Styles)</h3>
                    <Badge variant="secondary">{sizeStats.styles.count} 个</Badge>
                  </div>
                  <p className="text-sm text-orange-700">
                    总大小: <span className="font-mono font-bold">{formatSize(sizeStats.styles.size)}</span>
                  </p>
                </div>

                {/* 模板统计 */}
                <div className="p-4 bg-pink-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-pink-900">配图模板 (Templates)</h3>
                    <Badge variant="secondary">{sizeStats.templates.count} 个</Badge>
                  </div>
                  <p className="text-sm text-pink-700">
                    总大小: <span className="font-mono font-bold">{formatSize(sizeStats.templates.size)}</span>
                  </p>
                </div>

                {/* 槽位统计 */}
                <div className="p-4 bg-cyan-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-cyan-900">配图槽位 (Slots)</h3>
                    <Badge variant="secondary">{sizeStats.slots.count} 个</Badge>
                  </div>
                  <p className="text-sm text-cyan-700">
                    总大小: <span className="font-mono font-bold">{formatSize(sizeStats.slots.size)}</span>
                  </p>
                </div>
              </div>

              {/* 总计 */}
              <div className="p-4 bg-gray-100 rounded-lg border-2 border-gray-300">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-gray-900 text-lg">客户端估算总计</h3>
                  <span className="font-mono font-bold text-2xl text-gray-900">
                    {formatSize(totalSize)}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  ⚠️ 注意：这是客户端的粗略估算。服务器实际存储占用可能包含索引、元数据等，会比这个数字大很多。
                </p>
                <p className="text-xs text-red-600 mt-1">
                  🔴 Instant DB 显示占用: 2.02 GB（服务器端真实值）
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="h-32 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">扫描数据库中...</span>
        </div>
      ) : (
        <>
          <div className="flex gap-3">
            <Button
              onClick={() => setShouldScan(true)}
              disabled={isCleaning}
              variant="outline"
              className="gap-2"
            >
              <RefreshCcw className="w-4 h-4" />
              重新扫描
            </Button>

            <div className="flex gap-2 items-center">
              {(report.orphanedAssets > 0 || report.orphanedNotes > 0 || report.orphanedTemplates > 0 || report.orphanedSlots > 0) ? (
                <Button
                  variant="destructive"
                  onClick={cleanupDatabase}
                  disabled={isCleaning}
                  className="gap-2"
                >
                  {isCleaning && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isCleaning ? '清理中...' : '清理孤立数据'}
                  <Trash2 className="w-4 h-4" />
                </Button>
              ) : (
                <div className="p-3 bg-green-50 rounded-lg border border-green-200 flex-1">
                  <p className="text-sm text-green-700">
                    ✅ 客户端查询：没有发现孤立数据
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    但 Instant DB 控制台可能显示更多历史数据，请使用下方的"强力清理"功能
                  </p>
                </div>
              )}
            </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>孤立数据扫描</CardTitle>
              <CardDescription>
                找出所有不属于现有项目的素材和笔记（Instant DB 控制台可能显示更多历史数据）
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
            {/* 当前项目信息 */}
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm font-semibold text-blue-900 mb-2">当前活跃项目：</p>
              {projects.length === 0 ? (
                <p className="text-sm text-blue-700">无项目</p>
              ) : (
                <ul className="space-y-1">
                  {projects.map((project: any) => (
                    <li key={project.id} className="text-sm text-blue-800">
                      • {project.title} <span className="text-xs text-blue-600 font-mono">({project.id})</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">孤立素材</p>
                <Badge variant={report.orphanedAssets > 0 ? 'destructive' : 'secondary'} className="text-lg px-3 py-1">
                  {report.orphanedAssets}
                </Badge>
                <p className="text-xs text-muted-foreground">
                  (总数: {assets.length})
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">孤立笔记</p>
                <Badge variant={report.orphanedNotes > 0 ? 'destructive' : 'secondary'} className="text-lg px-3 py-1">
                  {report.orphanedNotes}
                </Badge>
                <p className="text-xs text-muted-foreground">
                  (总数: {notes.length})
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">孤立模板</p>
                <Badge variant={report.orphanedTemplates > 0 ? 'destructive' : 'secondary'} className="text-lg px-3 py-1">
                  {report.orphanedTemplates}
                </Badge>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">孤立槽位</p>
                <Badge variant={report.orphanedSlots > 0 ? 'destructive' : 'secondary'} className="text-lg px-3 py-1">
                  {report.orphanedSlots}
                </Badge>
              </div>
            </div>

            {report.orphanedAssets === 0 && report.orphanedNotes === 0 && report.orphanedTemplates === 0 && report.orphanedSlots === 0 && (
              <div className="text-center py-8">
                <p className="text-green-600 font-medium">✨ 数据库很干净，没有发现孤立数据！</p>
              </div>
            )}

            {/* 详细列表 */}
            {report.details.assets.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium text-sm">孤立素材列表：</p>
                <div className="text-sm text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                  {report.details.assets.map((asset: any) => (
                    <div key={asset.id} className="flex justify-between">
                      <span>{asset.filename}</span>
                      <span className="text-xs">项目ID: {asset.project_id}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {report.details.notes.length > 0 && (
              <div className="space-y-2">
                <p className="font-medium text-sm">孤立笔记列表：</p>
                <div className="text-sm text-muted-foreground space-y-1 max-h-32 overflow-y-auto">
                  {report.details.notes.map((note: any) => (
                    <div key={note.id} className="flex justify-between">
                      <span>{note.title}</span>
                      <span className="text-xs">项目ID: {note.project_id}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 智能清理 - 仅删除孤立数据 */}
        <Card className="border-green-200 bg-green-50/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-green-600" />
              <CardTitle className="text-green-900">🎯 智能清理（推荐）</CardTitle>
            </div>
            <CardDescription className="text-green-800">
              仅删除不属于当前项目的素材和笔记，保留你需要的数据。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 bg-white rounded border border-green-200">
              <p className="text-sm text-green-900 mb-2">
                <strong>清理策略：</strong>
              </p>
              <ul className="text-sm text-green-800 space-y-1">
                <li>✅ 保留: {projects.length} 个项目及其关联数据</li>
                <li>✅ 保留: 所有风格和模板配置</li>
                <li>❌ 删除: {report.orphanedAssets} 个孤立素材</li>
                <li>❌ 删除: {report.orphanedNotes} 个孤立笔记</li>
              </ul>
              {projects.length > 0 && (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <p className="text-xs text-green-700">
                    将保留项目：<strong>{projects.map((p: any) => p.title).join('、')}</strong>
                  </p>
                </div>
              )}
            </div>

            <Button
              variant="default"
              onClick={async () => {
                if (report.orphanedAssets === 0 && report.orphanedNotes === 0) {
                  toast.info('客户端查询：没有孤立数据需要清理')
                  return
                }

                const confirmed = confirm(
                  '🎯 智能清理（安全模式）\n\n' +
                  '这将：\n' +
                  `✅ 保留: ${projects.length} 个项目\n` +
                  `✅ 保留: 项目关联的素材和笔记\n` +
                  `❌ 删除: ${report.orphanedAssets} 个孤立素材\n` +
                  `❌ 删除: ${report.orphanedNotes} 个孤立笔记\n\n` +
                  `确定继续吗？`
                )
                
                if (!confirmed) return

                setIsCleaning(true)
                try {
                  const txns: any[] = []

                  // 删除孤立数据
                  report.details.assets.forEach((asset: any) => {
                    console.log(`删除孤立素材: ${asset.filename}`)
                    txns.push(db.tx.assets[asset.id].delete())
                  })

                  report.details.notes.forEach((note: any) => {
                    console.log(`删除孤立笔记: ${note.title}`)
                    txns.push(db.tx.notes[note.id].delete())
                  })

                  report.details.templates.forEach((tpl: any) => {
                    txns.push(db.tx.note_templates[tpl.id].delete())
                  })

                  report.details.slots.forEach((slot: any) => {
                    txns.push(db.tx.note_image_slots[slot.id].delete())
                  })

                  if (txns.length > 0) {
                    await batchTransact(txns)
                    toast.success(`智能清理完成：已删除 ${txns.length} 条孤立数据`)
                  } else {
                    toast.info('没有孤立数据需要清理')
                  }
                } catch (error) {
                  console.error(error)
                  toast.error(`清理失败: ${error instanceof Error ? error.message : '未知错误'}`)
                } finally {
                  setIsCleaning(false)
                }
              }}
              disabled={isCleaning}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {isCleaning && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              🎯 智能清理孤立数据
            </Button>
          </CardContent>
        </Card>

        {/* 强力清理 - 删除所有素材和笔记 */}
        <Card className="border-orange-200 bg-orange-50/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              <CardTitle className="text-orange-900">⚡ 强力清理（慎用）</CardTitle>
            </div>
            <CardDescription className="text-orange-800">
              如果 Instant DB 控制台显示大量数据（如 454 个 assets），但客户端查不到，使用此功能强制清空。
              <strong className="block mt-1">⚠️ 这将删除所有项目的所有数据！</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 bg-white rounded border border-orange-200">
              <p className="text-sm text-orange-900 mb-2">
                <strong>⚠️ 危险操作：</strong>
              </p>
              <ul className="text-sm text-orange-800 space-y-1">
                <li>• 客户端查询: {assets.length} 个素材, {notes.length} 个笔记</li>
                <li>• Instant DB 控制台可能显示更多（历史数据）</li>
                <li>• 此功能会删除所有素材和笔记（包括当前项目的）</li>
                <li>• 仅保留项目框架和配置</li>
              </ul>
            </div>

            <Button
              variant="destructive"
              onClick={async () => {
                const confirmed = confirm(
                  '⚠️ 强力清理模式（危险）\n\n' +
                  '这将：\n' +
                  `❌ 删除所有素材 (${assets.length}+ 个)\n` +
                  `❌ 删除所有笔记 (${notes.length}+ 个)\n` +
                  `❌ 包括当前项目的数据\n` +
                  `✅ 保留项目、风格、模板配置\n\n` +
                  `⚠️ 此操作不可恢复！确定继续吗？`
                )
                
                if (!confirmed) return

                const input = prompt('请输入 "CLEAR" 确认强力清理：')
                if (input !== 'CLEAR') {
                  toast.error('输入不正确，已取消操作')
                  return
                }

                setIsCleaning(true)
                try {
                  const txns: any[] = []
                  
                  // 删除所有素材
                  console.log(`准备删除 ${assets.length} 个素材...`)
                  assets.forEach((asset: any) => {
                    txns.push(db.tx.assets[asset.id].delete())
                  })
                  
                  // 删除所有笔记
                  console.log(`准备删除 ${notes.length} 个笔记...`)
                  notes.forEach((note: any) => {
                    txns.push(db.tx.notes[note.id].delete())
                  })

                  console.log(`总计 ${txns.length} 个删除操作`)
                  
                  if (txns.length > 0) {
                    await batchTransact(txns)
                    toast.success(`已删除 ${txns.length} 条数据`)
                  } else {
                    toast.info('没有需要删除的数据')
                  }
                } catch (error) {
                  console.error(error)
                  toast.error(`删除失败: ${error instanceof Error ? error.message : '未知错误'}`)
                } finally {
                  setIsCleaning(false)
                }
              }}
              disabled={isCleaning}
              className="w-full"
            >
              {isCleaning && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              ⚡ 强力清理所有素材和笔记
            </Button>
          </CardContent>
        </Card>

        {/* 危险区域 - 删除所有数据 */}
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <CardTitle className="text-red-900">☢️ 核弹级清理（极度危险）</CardTitle>
            </div>
            <CardDescription className="text-red-800">
              完全清空整个数据库，包括所有项目、素材、笔记、模板、槽位。
              <strong className="block mt-1">⚠️ 仅在需要完全重置数据库时使用！</strong>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-3 bg-white rounded border border-red-300">
              <p className="text-sm text-red-900 mb-2">
                <strong>☢️ 极度危险：</strong>
              </p>
              <ul className="text-sm text-red-800 space-y-1">
                <li>❌ 删除 {projects.length} 个项目</li>
                <li>❌ 删除 {assets.length} 个素材</li>
                <li>❌ 删除 {notes.length} 个笔记</li>
                <li>❌ 删除 {templates.length} 个模板</li>
                <li>❌ 删除 {slots.length} 个槽位</li>
                <li>❌ 删除所有配置和风格</li>
              </ul>
              <p className="text-xs text-red-600 mt-2 font-bold">
                数据库将变为完全空白状态！
              </p>
            </div>

            <Button
              variant="destructive"
              onClick={async () => {
                const confirmed = confirm(
                  '☢️ 核弹级清理！\n\n' +
                  `这将删除：\n` +
                  `- ${projects.length} 个项目（包括你当前的项目）\n` +
                  `- ${assets.length} 个素材\n` +
                  `- ${notes.length} 个笔记\n` +
                  `- ${templates.length} 个模板\n` +
                  `- ${slots.length} 个槽位\n\n` +
                  `⚠️ 数据库将完全清空！\n` +
                  `⚠️ 此操作不可恢复！\n\n` +
                  `确定继续吗？`
                )
                
                if (!confirmed) return
                
                const input = prompt('请输入 "DELETE ALL" 确认清空整个数据库：')
                if (input !== 'DELETE ALL') {
                  toast.error('输入不正确，已取消操作')
                  return
                }

                setIsCleaning(true)
                try {
                  const txns: any[] = []
                  
                  // 删除所有数据
                  projects.forEach((project: any) => {
                    txns.push(db.tx.projects[project.id].delete())
                  })
                  assets.forEach((asset: any) => {
                    txns.push(db.tx.assets[asset.id].delete())
                  })
                  notes.forEach((note: any) => {
                    txns.push(db.tx.notes[note.id].delete())
                  })
                  templates.forEach((template: any) => {
                    txns.push(db.tx.note_templates[template.id].delete())
                  })
                  slots.forEach((slot: any) => {
                    txns.push(db.tx.note_image_slots[slot.id].delete())
                  })

                  await batchTransact(txns)
                  toast.success('已清空整个数据库')
                } catch (error) {
                  console.error(error)
                  toast.error('删除失败')
                } finally {
                  setIsCleaning(false)
                }
              }}
              disabled={isCleaning}
              className="w-full bg-red-600 hover:bg-red-700"
            >
              {isCleaning && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              ☢️ 清空整个数据库
            </Button>
            
            <p className="text-xs text-red-600">
              💡 提示：删除后，Instant DB 的存储占用可能需要 5-15 分钟才会更新。如果还是很大，可能需要联系 Instant DB 支持清理历史数据。
            </p>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  )
}

